/**
 * Billing > Invoices > one invoice (Phase 21 / Task 21.7c).
 *
 *   DRAFT  regenerate (charges rebuilt, adjustments kept), add or remove an adjustment, notes,
 *          delete (frees the month), Send (number, PDF, email; the number is final)
 *   SENT   resend, payment reminder, void with a reason (optionally telling the customer), PDF
 *   PAID / VOID  the PDF only
 *
 * Every route is /v1/platform/billing/invoices/:id/... (invoices.ts, invoice-delivery.ts).
 * A send whose email failed is still SENT and numbered: the page says so, and Resend retries.
 */
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBackTo } from '../../lib/url-state';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { authedFetch, useApiOpts } from '@lodex-pro/web-shared';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import { useInvoice, useInvoiceMutation, type Delivery, type Invoice } from './billing-hooks';
import { billingErrorText } from './billing-format';
import { INVOICE_STATUS_LABELS, INVOICE_STATUS_TONES, money } from './invoice-format';
import { priceError } from './rate-card-draft';
import { InvoicePayments } from './InvoicePayments';
import { InvoiceOnlinePay } from './InvoiceOnlinePay';

const box: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  padding: '16px',
  marginBottom: '14px',
};

const fail = (what: string) => (err: unknown) =>
  toast.error(billingErrorText(err, `Could not ${what}.`));

/** "$1.00" style check for a SIGNED adjustment (a credit is negative); never $0. */
export function adjustmentError(raw: string): string | null {
  const t = raw.trim();
  const neg = t.startsWith('-');
  const e = priceError(neg ? t.slice(1) : t);
  if (e !== null) return e;
  if (/^-?\$?0*(\.0*)?$/.test(t.replace(/,/g, ''))) return 'Cannot be $0';
  return null;
}

function deliveryText(d: Delivery): string {
  return d.status === 'FAILED'
    ? `The invoice is sent and numbered, but the email failed (${d.stage ?? 'unknown stage'}): ${d.error ?? ''}. Use Resend.`
    : 'Emailed.';
}

const LAST_EMAIL_LABELS: Readonly<Record<NonNullable<Invoice['lastEmail']>['kind'], string>> = {
  INVOICE: 'Invoice last emailed',
  REMINDER: 'Payment reminder last emailed',
  VOID_NOTICE: 'Void notice emailed',
};

/** "To a@x, b@x; CC c@x". */
export function recipientText(to: readonly string[], cc: readonly string[]): string {
  return `To ${to.join(', ')}${cc.length > 0 ? `; CC ${cc.join(', ')}` : ''}`;
}

/**
 * #949 review MED-1: a send, resend or reminder goes to the billing account's CURRENT
 * addresses (not the bill-to snapshot taken when the draft was made), so every confirm names
 * those, and says plainly when there is no To (the server refuses NO_RECIPIENT; nothing falls
 * back to the tenant's primary email).
 */
function RecipientNote({ inv, action }: { inv: Invoice; action: string }): React.JSX.Element {
  const { to, cc } = inv.recipients;
  return (
    <p role="alert" data-testid="confirm-recipients" style={{ ...subText, marginTop: 0 }}>
      {to.length === 0
        ? 'This customer’s billing account has no To address, so this would be refused. Add one under Billing > Customers first.'
        : `${action} ${recipientText(to, cc)}.`}
    </p>
  );
}

function Totals({ inv }: { inv: Invoice }): React.JSX.Element {
  const balance = inv.totalCents - inv.amountPaidCents;
  return (
    <table style={{ marginLeft: 'auto', fontSize: '14px' }}>
      <tbody>
        <tr>
          <td style={{ padding: '2px 12px', ...subText }}>Subtotal</td>
          <td style={{ textAlign: 'right' }}>{money(inv.subtotalCents)}</td>
        </tr>
        <tr>
          <td style={{ padding: '2px 12px', ...subText }}>Tax</td>
          <td style={{ textAlign: 'right' }}>{money(inv.taxCents)}</td>
        </tr>
        <tr>
          <td style={{ padding: '2px 12px', fontWeight: 600 }}>Total</td>
          <td style={{ textAlign: 'right', fontWeight: 600 }} data-testid="invoice-total">
            {money(inv.totalCents)}
          </td>
        </tr>
        {inv.status !== 'DRAFT' && (
          <>
            <tr>
              <td style={{ padding: '2px 12px', ...subText }}>Paid</td>
              <td style={{ textAlign: 'right' }}>{money(inv.amountPaidCents)}</td>
            </tr>
            <tr>
              <td style={{ padding: '2px 12px', ...subText }}>Balance</td>
              <td style={{ textAlign: 'right' }} data-testid="invoice-balance">
                {money(balance)}
              </td>
            </tr>
          </>
        )}
      </tbody>
    </table>
  );
}

function AddAdjustment({ id }: { id: string }): React.JSX.Element {
  const [description, setDescription] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [reason, setReason] = React.useState('');
  const add = useInvoiceMutation<{ description: string; amount: string; reason: string }, Invoice>(
    `/invoices/${id}/adjustments`,
    'POST',
    {
      onSuccess: () => {
        setDescription('');
        setAmount('');
        setReason('');
        toast.success('Adjustment added.');
      },
      onError: fail('add the adjustment'),
    },
  );
  const amountErr = amount.trim() === '' ? null : adjustmentError(amount);
  const ready =
    description.trim() !== '' && reason.trim() !== '' && amount.trim() !== '' && amountErr === null;
  return (
    <form
      onSubmit={(e): void => {
        e.preventDefault();
        if (ready) {
          add.mutate({
            description: description.trim(),
            amount: amount.trim(),
            reason: reason.trim(),
          });
        }
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 2fr auto',
        gap: '8px',
        alignItems: 'end',
      }}
    >
      <FormRow label="Adjustment" htmlFor="adj-desc">
        <input
          id="adj-desc"
          value={description}
          onChange={(e): void => setDescription(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <FormRow label="Amount ($, negative for a credit)" htmlFor="adj-amount">
        <input
          id="adj-amount"
          value={amount}
          onChange={(e): void => setAmount(e.target.value)}
          style={fieldStyle}
          aria-invalid={amountErr !== null}
        />
        {amountErr !== null && (
          <p role="alert" style={{ color: 'var(--color-danger)', fontSize: '12px' }}>
            {amountErr}
          </p>
        )}
      </FormRow>
      <FormRow label="Reason (audited)" htmlFor="adj-reason">
        <input
          className="keep-case"
          id="adj-reason"
          value={reason}
          onChange={(e): void => setReason(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <Button type="submit" disabled={!ready || add.isPending}>
        Add
      </Button>
    </form>
  );
}

function RemoveAdjustment({ id, lineId }: { id: string; lineId: string }): React.JSX.Element {
  const remove = useInvoiceMutation<Record<string, never>, Invoice>(
    `/invoices/${id}/adjustments/${lineId}`,
    'DELETE',
    { onSuccess: () => toast.success('Adjustment removed.'), onError: fail('remove it') },
  );
  return (
    <Button type="button" variant="ghost" onClick={(): void => remove.mutate({})}>
      Remove
    </Button>
  );
}

function DraftActions({ inv }: { inv: Invoice }): React.JSX.Element {
  const navigate = useNavigate();
  const [confirmSend, setConfirmSend] = React.useState(false);
  const [notes, setNotes] = React.useState(inv.notes ?? '');
  const regenerate = useInvoiceMutation<Record<string, never>, Invoice>(
    `/invoices/${inv.id}/regenerate`,
    'POST',
    {
      onSuccess: () => toast.success('Charges rebuilt from the latest usage.'),
      onError: fail('regenerate'),
    },
  );
  const saveNotes = useInvoiceMutation<{ notes: string | null }, Invoice>(
    `/invoices/${inv.id}`,
    'PATCH',
    {
      onSuccess: () => toast.success('Notes saved.'),
      onError: fail('save the notes'),
    },
  );
  const remove = useInvoiceMutation<Record<string, never>, null>(`/invoices/${inv.id}`, 'DELETE', {
    onSuccess: () => {
      toast.success('Draft deleted. The month can be invoiced again.');
      void navigate('/billing/invoices');
    },
    onError: fail('delete the draft'),
  });
  const send = useInvoiceMutation<Record<string, never>, Invoice & { delivery: Delivery }>(
    `/invoices/${inv.id}/send`,
    'POST',
    { onSuccess: () => setConfirmSend(false), onError: fail('send the invoice') },
  );
  return (
    <section style={box}>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {confirmSend ? (
          <>
            <Button
              type="button"
              disabled={send.isPending || inv.recipients.to.length === 0}
              onClick={(): void =>
                send.mutate(
                  {},
                  {
                    onSuccess: (res) =>
                      res.delivery.status === 'FAILED'
                        ? toast.error(deliveryText(res.delivery))
                        : toast.success(`Sent as ${res.invoiceNumber ?? 'invoice'}.`),
                  },
                )
              }
            >
              Confirm send
            </Button>
            <Button type="button" variant="ghost" onClick={(): void => setConfirmSend(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button type="button" onClick={(): void => setConfirmSend(true)}>
            Send
          </Button>
        )}
        <Button type="button" variant="outline" onClick={(): void => regenerate.mutate({})}>
          Regenerate charges
        </Button>
        <Button type="button" variant="ghost" onClick={(): void => remove.mutate({})}>
          Delete draft
        </Button>
      </div>
      {confirmSend && (
        <>
          <RecipientNote
            inv={inv}
            action="Sending numbers the invoice, renders its PDF and emails"
          />
          <p style={{ ...subText, marginTop: 0 }}>
            The number and totals can never change after this; a mistake is a void and a new
            invoice.
          </p>
        </>
      )}
      <AddAdjustment id={inv.id} />
      <form
        onSubmit={(e): void => {
          e.preventDefault();
          saveNotes.mutate({ notes: notes.trim() === '' ? null : notes.trim() });
        }}
        style={{ display: 'flex', gap: '8px', alignItems: 'end', marginTop: '12px' }}
      >
        <FormRow label="Notes (not printed)" htmlFor="inv-notes">
          <input
            className="keep-case"
            id="inv-notes"
            value={notes}
            onChange={(e): void => setNotes(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <Button type="submit" variant="outline">
          Save notes
        </Button>
      </form>
    </section>
  );
}

function SentActions({ inv }: { inv: Invoice }): React.JSX.Element {
  const [voiding, setVoiding] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [notify, setNotify] = React.useState(false);
  // #949 review MED-2: both email the customer, so each asks first and runs one at a time.
  const [confirming, setConfirming] = React.useState<'RESEND' | 'REMIND' | null>(null);
  const resend = useInvoiceMutation<Record<string, never>, Invoice & { delivery: Delivery }>(
    `/invoices/${inv.id}/resend`,
    'POST',
    { onSuccess: () => setConfirming(null), onError: fail('resend') },
  );
  const remind = useInvoiceMutation<Record<string, never>, { reminder: Delivery }>(
    `/invoices/${inv.id}/remind`,
    'POST',
    { onSuccess: () => setConfirming(null), onError: fail('send the reminder') },
  );
  const emailing = resend.isPending || remind.isPending;
  const noTo = inv.recipients.to.length === 0;
  const doVoid = useInvoiceMutation<{ reason: string; notifyCustomer: boolean }, Invoice>(
    `/invoices/${inv.id}/void`,
    'POST',
    {
      onSuccess: () => {
        setVoiding(false);
        toast.success('Voided. The month can be invoiced again.');
      },
      onError: fail('void the invoice'),
    },
  );
  return (
    <section style={box}>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {confirming === null ? (
          <>
            <Button
              type="button"
              variant="outline"
              disabled={emailing}
              onClick={(): void => setConfirming('RESEND')}
            >
              Resend
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={emailing}
              onClick={(): void => setConfirming('REMIND')}
            >
              Send payment reminder
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              disabled={emailing || noTo}
              onClick={(): void =>
                confirming === 'RESEND'
                  ? resend.mutate(
                      {},
                      {
                        onSuccess: (res) =>
                          res.delivery.status === 'FAILED'
                            ? toast.error(deliveryText(res.delivery))
                            : toast.success('Emailed again.'),
                      },
                    )
                  : remind.mutate(
                      {},
                      {
                        onSuccess: (res) =>
                          res.reminder.status === 'FAILED'
                            ? toast.error(`The reminder failed: ${res.reminder.error ?? ''}`)
                            : toast.success('Payment reminder sent.'),
                      },
                    )
              }
            >
              {confirming === 'RESEND' ? 'Confirm resend' : 'Confirm reminder'}
            </Button>
            <Button type="button" variant="ghost" onClick={(): void => setConfirming(null)}>
              Cancel
            </Button>
          </>
        )}
        {!voiding && (
          <Button type="button" variant="ghost" onClick={(): void => setVoiding(true)}>
            Void
          </Button>
        )}
      </div>
      {confirming !== null && (
        <RecipientNote
          inv={inv}
          action={
            confirming === 'RESEND'
              ? 'This emails the invoice again, with its PDF,'
              : 'This emails a payment reminder for the balance'
          }
        />
      )}
      {voiding && (
        <form
          onSubmit={(e): void => {
            e.preventDefault();
            if (reason.trim() !== '')
              doVoid.mutate({ reason: reason.trim(), notifyCustomer: notify });
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}
        >
          <FormRow label="Why it is void (audited)" htmlFor="void-reason" required>
            <input
              className="keep-case"
              id="void-reason"
              value={reason}
              onChange={(e): void => setReason(e.target.value)}
              style={fieldStyle}
            />
          </FormRow>
          <label style={{ ...subText, display: 'flex', gap: '6px', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={notify}
              onChange={(e): void => setNotify(e.target.checked)}
            />
            Email the customer that this invoice is void
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              type="submit"
              variant="destructive"
              disabled={reason.trim() === '' || doVoid.isPending}
            >
              Void invoice
            </Button>
            <Button type="button" variant="ghost" onClick={(): void => setVoiding(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

function PdfButton({ id }: { id: string }): React.JSX.Element {
  const opts = useApiOpts();
  return (
    <Button
      type="button"
      variant="outline"
      onClick={(): void => {
        // The URL is presigned for five minutes, so it is fetched when pressed, never cached.
        void authedFetch(opts, `/v1/platform/billing/invoices/${id}/pdf`)
          .then(async (res) => {
            if (!res.ok) throw new Error(String(res.status));
            const body = (await res.json()) as { url: string };
            window.open(body.url, '_blank', 'noopener');
          })
          .catch(() => toast.error('Could not open the PDF.'));
      }}
    >
      Open PDF
    </Button>
  );
}

export function InvoiceDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // OPEN-191 — Back returns to the list it came from: Invoices, Payments or Aging, filters kept.
  const backTo = useBackTo('/billing/invoices', {
    allowed: ['/billing/payments', '/billing/aging'],
  });
  const backLabel = backTo.startsWith('/billing/payments')
    ? 'Payments'
    : backTo.startsWith('/billing/aging')
      ? 'Aging'
      : 'Invoices';
  const { data: inv, isLoading, isError } = useInvoice(id);
  if (isLoading) return <p style={subText}>Loading…</p>;
  if (isError || inv === undefined) {
    return (
      <p role="alert" style={{ color: 'var(--color-danger)' }}>
        Could not load this invoice.
      </p>
    );
  }
  const draft = inv.status === 'DRAFT';
  return (
    <div style={{ maxWidth: '920px' }}>
      <Button
        type="button"
        variant="ghost"
        onClick={(): void => {
          void navigate(backTo);
        }}
      >
        <ArrowLeft size={16} aria-hidden="true" /> {backLabel}
      </Button>
      <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '8px 0 4px' }}>
        {inv.invoiceNumber ?? 'Draft invoice'}{' '}
        <Pill tone={INVOICE_STATUS_TONES[inv.status]} label={INVOICE_STATUS_LABELS[inv.status]} />
      </h2>
      <p style={{ ...subText, marginTop: 0 }}>
        {inv.customerName ?? inv.customerTenantId} · {inv.periodStart} to {inv.periodEnd}
        {inv.issueDate !== null && ` · issued ${inv.issueDate}`}
        {inv.dueDate !== null && ` · due ${inv.dueDate}`}
      </p>
      {inv.emailStatus === 'FAILED' && (
        <p role="alert" data-testid="email-failed" style={{ color: 'var(--color-danger)' }}>
          The invoice is sent and numbered, but its email failed:{' '}
          {inv.emailError ?? 'unknown error'}. Use Resend.
        </p>
      )}
      {inv.status === 'VOID' && (
        <p style={subText} data-testid="void-reason">
          Void: {inv.voidReason}
        </p>
      )}
      {(inv.warnings ?? []).map((w) => (
        <p key={w.code} style={{ ...subText, color: 'var(--color-warning)' }}>
          {w.message}
        </p>
      ))}
      {draft && <DraftActions inv={inv} />}
      {inv.status === 'SENT' && <SentActions inv={inv} />}
      {!draft && inv.pdf !== null && (
        <div style={{ marginBottom: '14px' }}>
          <PdfButton id={inv.id} />
        </div>
      )}
      <section style={box}>
        {inv.billTo !== null && (
          <p style={{ ...subText, marginTop: 0 }}>
            Bill to {inv.billTo.name}
            {inv.billTo.poReference !== null && ` · PO ${inv.billTo.poReference}`}
          </p>
        )}
        {inv.lastEmail !== null && (
          // #949 review MED-1: who the last email actually went to, as its delivery recorded
          // it (the account's addresses may have changed since).
          <p data-testid="last-email" style={{ ...subText, marginTop: 0 }}>
            {LAST_EMAIL_LABELS[inv.lastEmail.kind]} {inv.lastEmail.at.slice(0, 10)}
            {inv.lastEmail.to === null
              ? ' (recipients not recorded)'
              : ` · ${recipientText(inv.lastEmail.to, inv.lastEmail.cc ?? [])}`}
          </p>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ textAlign: 'left', ...subText }}>
              <th style={{ padding: '4px 6px' }}>Description</th>
              <th style={{ padding: '4px 6px' }}>Quantity</th>
              <th style={{ padding: '4px 6px', textAlign: 'right' }}>Unit</th>
              <th style={{ padding: '4px 6px', textAlign: 'right' }}>Amount</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l) => (
              <tr key={l.id} style={{ borderTop: '1px solid var(--color-border-primary)' }}>
                <td style={{ padding: '6px' }}>
                  {l.description}
                  {l.kind === 'ADJUSTMENT' && l.reason !== null && (
                    <div style={subText}>Adjustment: {l.reason}</div>
                  )}
                </td>
                <td style={{ padding: '6px' }}>{l.quantity}</td>
                <td style={{ padding: '6px', textAlign: 'right' }}>{money(l.unitPriceCents)}</td>
                <td style={{ padding: '6px', textAlign: 'right' }}>{money(l.amountCents)}</td>
                <td style={{ padding: '6px' }}>
                  {draft && l.kind === 'ADJUSTMENT' && (
                    <RemoveAdjustment id={inv.id} lineId={l.id} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Totals inv={inv} />
      </section>
      {/* 21.7d: the ledger, recording money received, and reversals. */}
      {!draft && <InvoicePayments inv={inv} />}
      {/* OPEN-212 PR B: pay by bank through Stripe (test mode). */}
      {!draft && <InvoiceOnlinePay inv={inv} />}
    </div>
  );
}
